import CloseButton from '@atoms/CloseButton';
import SvgSafeGatsbyImage from '@atoms/SvgSafeGatsbyImage';
import { usePrimaryNav } from '@hooks/use-primary-nav';
import InstagramIcon from '@images/svg/instagram.svg';
import React, { useState } from 'react';
import {
    SOCIAL_URL_FACEBOOK,
    SOCIAL_URL_LINKEDIN,
    SOCIAL_URL_TWITTER,
    SOCIAL_URL_YOUTUBE,
    PHONE_NUMBER_DISPLAY,
} from '../../constants/constants';
import { useCtaProvider } from '../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../contexts/franchise/microsite-context.tsx';
import ArrowIcon from '../../images/svg/arrow.svg';
import FacebookIcon from '../../images/svg/facebook.svg';
import LinkedinIcon from '../../images/svg/linkedin.svg';
import TwitterIcon from '../../images/svg/twitter.svg';
import YoutubeIcon from '../../images/svg/youtube.svg';
import getLink from '../utils/get-link';
import SiteSearchBar from "../organisms/site-search-input";
import { useDniProvider, DniBrand } from '../../contexts/dni-provider';
import Link from "../primitives/link";
import Button, {buttonStyles} from "../primitives/button";
import { colors } from '../primitives/colors';

const GlobalNav = ({ isVisible, onClose }) => {
    const { nav_items: primaryNavLinks } = usePrimaryNav();
    const { isMicrosite, menu: micrositeMenu } = useMicrosite();
    const servproUrlRegex = /^(http(s?):\/\/)?(www\.)?servpro\.com\/?$/;
    const { provider: DniProvider } = useDniProvider();

    const navLinks = isMicrosite && micrositeMenu?.nav_items ? micrositeMenu?.nav_items.filter(navItem => DniProvider !== DniBrand.TELMETRICS || (DniProvider === DniBrand.TELMETRICS && !servproUrlRegex.test(navItem?.nav_item_link?.href || ''))) : primaryNavLinks;

    const navWrapRef = React.useRef(null);

    const handleDocumentKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, []);

    React.useEffect(() => {
        if (isVisible) {
            document.addEventListener('keydown', handleDocumentKeyDown);
        } else {
            document.removeEventListener('keydown', handleDocumentKeyDown);
        }

        return () => document.removeEventListener('keydown', handleDocumentKeyDown);
    }, [isVisible]);

    React.useEffect(() => {
        if (!navWrapRef.current) {
            return;
        }

        if (isVisible && navWrapRef.current) {
            navWrapRef.current.style.visibility = 'visible';
        } else {
            setTimeout(() => {
                if (navWrapRef.current) {
                    navWrapRef.current.style.visibility = 'hidden';
                }
            }, 300);
        }
    }, [isVisible]);

    return (
        <div css={globalNavStyles.navWrap(isVisible)} ref={navWrapRef}>
            <div css={globalNavStyles.hideOnMobile}>
                <GlobalNavDesktop navItems={navLinks} onClose={onClose} />
            </div>
            <div css={globalNavStyles.showOnMobile}>
                <GlobalNavMobile navItems={navLinks} onClose={onClose} />
            </div>
        </div>
    );
};

const globalNavStyles = {
    navWrap: (isVisible: boolean) => ({
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        willChange: "opacity",
        zIndex: 250,
        position: "fixed",
        top: "0",
        left: "0",
        visibility: "hidden",
        width: "100%",
        height: "100%",
        backgroundColor: colors.shadesWhite,
        transition: "opacity 300ms cubic-bezier(0, 0, 0.2, 1)",
        opacity: isVisible ? 1 : 0
    }),
    showOnMobile: {
        display: "block",
        "@media (min-width: 1024px)": {
            display: "none"
        }
    },
    hideOnMobile: {
        display: "none",
        "@media (min-width: 1024px)": {
            display: "block"
        }
    },

}

const GlobalNavDesktop = ({ onClose, navItems }) => {
    const {
        isMicrosite,
        micrositePermalink,
        franchise,
    } = useMicrosite();

    const [indexActiveItem, setIndexActiveItem] = useState(isMicrosite ? 1 : 0);
    const { hideNationalCallCenter } = useCtaProvider();

    const changeIndexActiveItem = (index) => {
        setIndexActiveItem(index);
    };

    function handleSearchbarFocus() {
        onClose();
    }


    return (
        <div css={globalNavDesktopStyles.leftOuterWrap}>
            <div css={globalNavDesktopStyles.leftInnerWrap}>
                <ul css={globalNavDesktopStyles.navItemsWrap}>
                    {navItems && navItems.map((navItem, index) => {
                        const { nav_item_link, columns = [] } = navItem;
                        if (!nav_item_link) return null;
                        const { href, title } = nav_item_link;

                        if (!href && columns.filter(x => x.group.filter(x => !!x).length > 0).length === 0) return null;

                        return (
                            <li
                                css={globalNavDesktopStyles.navListItem(index === indexActiveItem)}
                                key={index}
                                onClick={() => {
                                    if (columns.length > 0) {
                                        changeIndexActiveItem(index);
                                    } else {
                                        onClose();
                                    }
                                }}
                            >
                                {columns.length > 0 ? (
                                    <Button>
                                        {title}
                                        &nbsp;+
                                    </Button>
                                ) : (
                                    <Link
                                        css={buttonStyles}
                                        to={href}
                                    >
                                        {title}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
                </ul>
                <div css={globalNavDesktopStyles.searchbarOuterWrap}>
                    {!isMicrosite && (
                        <>
                            <div css={globalNavDesktopStyles.searchbarWrap}>
                                <SiteSearchBar
                                    onFocus={handleSearchbarFocus}
                                />
                            </div>
                            <div css={globalNavDesktopStyles.nationalCallCenterWrap}>
                                {!hideNationalCallCenter && (
                                    <>
                                        <p css={globalNavDesktopStyles.nationalCallCenterLabel}>National Call Center</p>
                                        <p css={globalNavDesktopStyles.nationalCallCenterNumber}>{PHONE_NUMBER_DISPLAY}</p>
                                    </>
                                )}
                                <p>801 Industrial Blvd Gallatin, TN 37066</p>
                            </div>
                        </>
                    )}
                    {!isMicrosite && (
                        <div css={globalNavDesktopStyles.iconWrap}>
                            <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={SOCIAL_URL_LINKEDIN} target="_blank">
                                <LinkedinIcon />
                            </Link>
                            <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={SOCIAL_URL_TWITTER} target="_blank">
                                <TwitterIcon />
                            </Link>
                            <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={SOCIAL_URL_YOUTUBE} target="_blank">
                                <YoutubeIcon />
                            </Link>
                            <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={SOCIAL_URL_FACEBOOK} target="_blank">
                                <FacebookIcon />
                            </Link>
                        </div>
                    )}
                    {isMicrosite && (
                        <div css={globalNavDesktopStyles.iconWrap}>
                            {franchise?.socialMedia?.linkedIn && (
                                <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={`https://linkedin.com/${franchise?.socialMedia?.linkedIn}`} target="_blank">
                                    <LinkedinIcon />
                                </Link>
                            )}
                            {franchise?.socialMedia?.twitter && (
                                <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={`https://twitter.com/${franchise?.socialMedia?.twitter}`} target="_blank">
                                    <TwitterIcon />
                                </Link>
                            )}
                            {franchise?.socialMedia?.instagram && (
                                <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={`https://instagram.com/${franchise?.socialMedia?.instagram}`} target="_blank">
                                    <InstagramIcon />
                                </Link>
                            )}
                            {franchise?.socialMedia?.facebook && (
                                <Link css={globalNavDesktopStyles.socialLink} onClick={onClose} to={`https://facebook.com/${franchise?.socialMedia?.facebook}`} target="_blank">
                                    <FacebookIcon />
                                </Link>
                            )}
                        </div>
                    )}
                    <div css={globalNavDesktopStyles.privacyAndTerms}>
                        <Link onClick={onClose} to="https://servpro.com/legal/privacy">Privacy Policy</Link>
                        <Link onClick={onClose} to="https://servpro.com/legal/terms">Terms of Service</Link>
                    </div>
                    <p css={globalNavDesktopStyles.copyright}>
                        ©
                        {(new Date()).getFullYear()}
                        {' '}
                        SERVPRO. All rights reserved.
                    </p>
                </div>
            </div>
            <div css={globalNavDesktopStyles.rightWrap}>
                <CloseButton css={globalNavDesktopStyles.closeButton} onClick={onClose} />

                {navItems && navItems.map((item, index) => {
                    const { columns } = item;

                    return (
                        !!item?.columns
                        && index === indexActiveItem && (
                            <div css={globalNavDesktopStyles.activeItemWrap}
                                key={index}>
                                {columns.map((col, columnIndex) => {
                                    const { group: groups } = col;

                                    return (
                                        <div css={globalNavDesktopStyles.linksCol} key={columnIndex}>
                                            {groups.map((group, groupIndex) => {
                                                if (!group) {
                                                    return null;
                                                }

                                                const { category_link: link, icon, subgroup } = group;

                                                if (!link || !link.title) {
                                                    return null;
                                                }

                                                return (
                                                    <React.Fragment key={groupIndex}>
                                                        {/* eslint-disable-next-line no-nested-ternary */}
                                                        {!!subgroup && subgroup.length > 0 ? (
                                                            <div css={globalNavDesktopStyles.linksGroupBase} key={link.title}>
                                                                {icon?.url ? (
                                                                    <Link css={globalNavDesktopStyles.linkWithIcon} onClick={onClose} to={getLink(link.href, isMicrosite && micrositePermalink)}>
                                                                        <SvgSafeGatsbyImage
                                                                            css={globalNavDesktopStyles.icon}
                                                                            image={icon}
                                                                            alt={link.title}
                                                                        />
                                                                        {link.title}
                                                                    </Link>
                                                                ) : (
                                                                    <div
                                                                        css={globalNavDesktopStyles.linkWithoutIconWrap}
                                                                    >
                                                                        {link.href ? (
                                                                            <Link css={globalNavDesktopStyles.linkWithoutIcon} onClick={onClose} to={getLink(link.href, isMicrosite && micrositePermalink)}>
                                                                                {link.title}
                                                                            </Link>
                                                                        ) : (
                                                                            <span css={globalNavDesktopStyles.textLinkWithoutIcon}>
                                                                                {link.title}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {subgroup.map(subgroupItem => (
                                                                    <div css={globalNavDesktopStyles.linksGroupWithHeading}
                                                                        key={subgroupItem.title}
                                                                    >
                                                                        {(subgroupItem.title || subgroupItem?.subgroup_link?.title) && !!subgroupItem.subgroup_link?.href && (
                                                                            <Link
                                                                                css={globalNavDesktopStyles.linksGroupLinkHeading}
                                                                                onClick={onClose}
                                                                                to={getLink(subgroupItem.subgroup_link.href, isMicrosite && micrositePermalink)}
                                                                                title={
                                                                                    subgroupItem.subgroup_link.title
                                                                                }
                                                                            >
                                                                                {(subgroupItem.title || subgroupItem?.subgroup_link?.title)}
                                                                            </Link>
                                                                        )}
                                                                        {subgroupItem.title !== ''
                                                                            && !subgroupItem.subgroup_link?.href && (
                                                                                <p css={globalNavDesktopStyles.linksGroupLinkHeading} onClick={onClose}>
                                                                                    {subgroupItem.title}
                                                                                </p>
                                                                            )}
                                                                        <div
                                                                            css={globalNavDesktopStyles.baseLinkWrap}
                                                                        >
                                                                            {(subgroupItem && subgroupItem.links) && subgroupItem.links.map(
                                                                                link => link && (
                                                                                    <Link css={globalNavDesktopStyles.baseLink} onClick={onClose} key={link?.title} to={getLink(link?.href, isMicrosite && micrositePermalink)}>
                                                                                        {link?.title}
                                                                                    </Link>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : icon?.url ? (
                                                            <>
                                                                <Link css={globalNavDesktopStyles.linkWithIcon} onClick={onClose} to={getLink(link.href, isMicrosite && micrositePermalink)}>
                                                                    <SvgSafeGatsbyImage
                                                                        css={globalNavDesktopStyles.icon}
                                                                        image={icon}
                                                                        alt={link.title}
                                                                    />
                                                                    {link.title}
                                                                </Link>
                                                                <div css={globalNavDesktopStyles.divider} />
                                                            </>
                                                        ) : (
                                                            <div
                                                                css={globalNavDesktopStyles.linkWithoutIconWrap}

                                                            >
                                                                {link.href ? (
                                                                    <Link css={globalNavDesktopStyles.linkWithoutIcon} to={getLink(link.href, isMicrosite && micrositePermalink)}>
                                                                        {link.title}
                                                                    </Link>
                                                                ) : (
                                                                    <span css={globalNavDesktopStyles.textLinkWithoutIcon}>
                                                                        {link.title}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    );
                })}
            </div>
        </div>
    );
};

const globalNavDesktopStyles = {
    leftOuterWrap: {
        display: "flex",
        minHeight: "100vh"
    },
    leftInnerWrap: {
        display: "flex",
        flexDirection: "column",
        background: colors.primaryOrange,
        color: colors.shadesWhite,
        paddingLeft: "8rem",
        paddingRight: "6rem",
        paddingTop: "5rem",
        paddingBottom: "2.5rem",
        "@media (min-width: 1025px)": {
            maxWidth: "612px",
            width: "100%",
            clipPath: "polygon(0% 0%, 74% 0%, 100% 50%, 74% 100%, 0% 100%)"
        }
    },
    navItemsWrap: {
        marginBottom: "4rem",
        '>:not([hidden])~:not([hidden])': {
            marginTop: "0.5rem"
        },
    },
    searchbarWrap: {
        paddingTop: "0.5rem",
        marginBottom: "3.5rem",
        maxWidth: '306px'
    },
    nationalCallCenterWrap: {
        marginBottom: "4rem",
        width: "12rem"
    },
    nationalCallCenterLabel: {
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        fontWeight: 700,
        marginBottom: "0.25rem"
    },
    nationalCallCenterNumber: {
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        fontWeight: 700,
        marginBottom: "1.5rem"
    },
    iconWrap: {
        display: "flex",
        marginBottom: "1.75rem",
        alignItems: "center",
        '>:not([hidden])~:not([hidden])': {
            marginLeft: "2.25rem"
        },
    },
    icon: {
        marginRight: "0.25rem",
        width: "3rem"
    },
    socialLink: {
        svg: {
            fill: colors.shadesWhite
        }
    },
    privacyAndTerms: {
        display: "flex",
        marginBottom: "2.25rem",
        fontSize: "0.75rem",
        lineHeight: "1rem",
        '>:not([hidden])~:not([hidden])': {
            marginLeft: "2rem"
        },
    },
    copyright: {
        fontSize: "0.75rem",
        lineHeight: "1rem"
    },
    closeButton: {
        top: "2.5rem",
        right: "4rem"
    },
    rightWrap: {
        paddingLeft: "4rem",
        paddingTop: "9rem"
    },
    activeItemWrap: {
        display: "grid",
        paddingLeft: "0.25rem",
        paddingTop: "1.25rem",
        rowGap: "1.25rem",
        "@media (min-width: 1280px)": {
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
        }
    },
    linksCol: {
        paddingLeft: "0.25rem",
        '&:empty': {
            display: 'none'
        },
        '>:not([hidden])~:not([hidden])': {
            marginTop: '1.5rem',
        },
        '&:last-of-type': {
            "@media (min-width: 1280px)": {
                marginLeft: "2.25rem"
            }
        }
    },
    linksGroupBase: {
        '>:not([hidden])~:not([hidden])': {
            marginTop: '0.25rem',
        }
    },
    linkWithIcon: {
        display: "flex",
        alignItems: "center",
        fontSize: "1.5rem",
        lineHeight: "2rem",
        fontWeight: 500,
        '&:focus': {
            outline: "2px solid transparent",
            outlineOffset: "2px"
        }
    },
    linkWithoutIconWrap: {
        marginLeft: "3.5rem",
        fontSize: "1.5rem",
        lineHeight: "2rem",
        fontWeight: 500
    },
    linkWithoutIcon: {
        display: "flex",
        outline: "2px solid transparent",
        outlineOffset: "2px"
    },
    textLinkWithoutIcon: {
        display: "flex",
        cursor: "default"
    },
    linksGroupWithHeading: {
        paddingLeft: "54px",
        paddingBottom: "10px"
    },
    linksGroupLinkHeading: {
        marginBottom: "0.75rem",
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        fontWeight: 600
    },
    baseLink: {
        display: "flex",
        paddingLeft: "0.25rem",
        fontSize: "1.125rem",
        lineHeight: "1.75rem"
    },
    baseLinkWrap: {
        paddingBottom: "0.25rem",
        paddingLeft: "1.25rem",
        '>:not([hidden])~:not([hidden])': {
            marginTop: "0.5rem"
        },
    },
    divider: {
        backgroundColor: "#f5f5f5",
        height: "1px",
        width: '100%'
    },
    navListItem: (isActive: boolean) => ({
        fontSize: "36px",
        opacity: 1,
        fontWeight: 350,
        button: {
            fontWeight: 350
        },
        "@media (min-width: 1280px)": {
            opacity: isActive ? 1 : 0.7,
            fontSize: "42px"
        },
    }),
    searchbarOuterWrap: {
        marginTop: "auto"
    },

};

const GlobalNavMobile = ({ onClose, navItems }) => {
    const {
        isMicrosite,
        micrositePermalink,
    } = useMicrosite();

    const [indexActiveItem, setIndexActiveItem] = useState(null);

    const changeIndexActiveItem = (index) => {
        setIndexActiveItem(index);
    };

    const returnIndexActiveItem = () => {
        setIndexActiveItem(null);
    };

    const handleSearchbarFocus = (): void => {
        onClose();
    }



    return (
        <div
            css={globalNavMobileStyles.globalNavWrap}
        >
            <CloseButton
                css={globalNavMobileStyles.closeButton}
                onClick={onClose} />
            <div
                css={globalNavMobileStyles.navItemsWrap}
            >
                <ul
                    css={globalNavMobileStyles.navItems}

                >
                    {navItems && navItems.map((navItem, index) => {
                        const { nav_item_link, columns = [] } = navItem;
                        if (!nav_item_link) return null;
                        const { href, title } = nav_item_link;

                        if (!href && columns.filter(x => x.group.filter(x => !!x).length > 0).length === 0) return null;

                        return (
                            <React.Fragment key={index}>
                                {columns.length > 0 ? (
                                    <li
                                        css={globalNavMobileStyles.navListItem(index === indexActiveItem)}
                                        key={index}
                                        onClick={() => changeIndexActiveItem(index)}
                                    >
                                        {title}
                                        &nbsp;+
                                    </li>
                                ) : (
                                    <li
                                        css={globalNavMobileStyles.navListItem(index === indexActiveItem)}
                                        key={index} >
                                        <Link onClick={onClose} to={href}>{title}</Link>
                                    </li>
                                )}
                            </React.Fragment>
                        );
                    })}
                </ul>
                {!isMicrosite && (
                    <>
                        <div
                            css={globalNavMobileStyles.siteSearchBarInnerWrap}
                        >
                            <SiteSearchBar
                                onFocus={handleSearchbarFocus}
                            />
                        </div>
                        <p
                            css={globalNavMobileStyles.phoneNumberDisplayWrap}

                        >
                            24/7 Nationwide
                            {' '}
                            <br />
                            {' '}
                            Service
                            {' '}
                            <span
                                css={globalNavMobileStyles.phoneNumberDisplay}
                            >{PHONE_NUMBER_DISPLAY}</span>
                        </p>
                    </>
                )}
            </div>
            {indexActiveItem !== null && (
                <div css={globalNavMobileStyles.activeSection}>
                    <h3 css={globalNavMobileStyles.activeSectionHeading} onClick={returnIndexActiveItem}>
                        <ArrowIcon css={globalNavMobileStyles.arrowIcon} />
                        {navItems[indexActiveItem].title}
                    </h3>
                    <div css={globalNavMobileStyles.activeItemWrap}>
                        {navItems && navItems[indexActiveItem].columns
                            && navItems[indexActiveItem].columns.length > 0
                            && navItems[indexActiveItem].columns.map(column => column.group.map(
                                (navItem) => {
                                    if (!navItem) return null;

                                    const { category_link, icon_inverse, subgroup } = navItem;

                                    return (
                                        <React.Fragment key={category_link.title}>
                                            {subgroup.length > 0 ? (
                                                <>
                                                    <div
                                                        css={globalNavMobileStyles.categoryLinkWrap}
                                                    >
                                                        {!!icon_inverse?.url && (
                                                            <SvgSafeGatsbyImage
                                                                css={globalNavMobileStyles.icon}
                                                                image={icon_inverse}
                                                                alt={category_link.title}
                                                            />
                                                        )}
                                                        <Link css={globalNavMobileStyles.categoryLink} onClick={onClose} to={getLink(category_link.href, isMicrosite && micrositePermalink)}>
                                                            {category_link.title}
                                                        </Link>
                                                    </div>
                                                    {subgroup.map(
                                                        ({ title, subgroup_link, links }, index) => {
                                                            const { href: linkHref, title: linkTitle } = subgroup_link;

                                                            return (
                                                                <React.Fragment key={index}>
                                                                    {(title || subgroup_link?.title) && !!linkHref && (
                                                                        <Link
                                                                            css={globalNavMobileStyles.subgroupLink}
                                                                            to={getLink(linkHref, isMicrosite && micrositePermalink)}
                                                                            title={linkTitle}
                                                                            onClick={onClose}
                                                                        >
                                                                            {(title || subgroup_link?.title)}
                                                                        </Link>
                                                                    )}
                                                                    {title !== '' && !linkHref && (
                                                                        <p css={globalNavMobileStyles.subgroupLink}>
                                                                            {title}
                                                                        </p>
                                                                    )}
                                                                    {links.map(
                                                                        (
                                                                            link,
                                                                            linkIndex,
                                                                        ) => link && (
                                                                            <Link
                                                                                onClick={onClose}
                                                                                css={globalNavMobileStyles.linkSmall}
                                                                                key={linkIndex}
                                                                                to={getLink(link.href, isMicrosite && micrositePermalink)}
                                                                            >
                                                                                {link.title}
                                                                            </Link>
                                                                        ),
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        },
                                                    )}
                                                </>
                                            ) : (
                                                <div
                                                    css={
                                                        globalNavMobileStyles.categoryLinkWrap
                                                    }
                                                >
                                                    {!!icon_inverse?.url && (
                                                        <SvgSafeGatsbyImage
                                                            css={globalNavMobileStyles.icon}
                                                            image={icon_inverse}
                                                            alt={category_link.title}
                                                        />
                                                    )}
                                                    <Link css={globalNavMobileStyles.categoryLink} onClick={onClose} to={getLink(category_link.href, isMicrosite && micrositePermalink)}>
                                                        {category_link.title}
                                                    </Link>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                },
                            ))}
                    </div>
                    {!isMicrosite && (
                        <div
                            css={globalNavMobileStyles.siteSearchBarOuterWrap}
                        >
                            <div
                                css={globalNavMobileStyles.siteSearchBarInnerWrap}
                            >
                                <SiteSearchBar
                                    onFocus={handleSearchbarFocus}
                                />
                            </div>
                            <p
                                css={globalNavMobileStyles.nationalCallCenterNumber}
                            >
                                24/7 Nationwide
                                {' '}
                                <br />
                                {' '}
                                Service
                                {' '}
                                <span
                                    css={globalNavMobileStyles.phoneNumberDisplay}
                                >{PHONE_NUMBER_DISPLAY}</span>
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const globalNavMobileStyles = {
    globalNavWrap: {
        backgroundColor: colors.primaryOrange,
        display: "flex",
        paddingTop: "7rem",
        flexDirection: "column",
        minHeight: "100vh",
        color: colors.shadesWhite
    },
    closeButton: {
        top: "1.25rem",
        right: "1.25rem",
        backgroundImage: "none"
    },
    navItemsWrap: {
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
        paddingBottom: "3.5rem"
    },
    navItems: {
        marginBottom: "2.5rem",
        '>:not([hidden])~:not([hidden])': {
            marginTop: "0.5rem"
        }
    },
    nationalCallCenterNumber: {
        fontSize: "0.875rem",
        lineHeight: "1.25rem",
        marginBottom: "3rem"
    },
    navListItem: (isActive: boolean) => ({
        fontSize: "36px",
        opacity: 1,
        fontWeight: 350,
        button: {
            fontWeight: 350
        },
        "@media (min-width: 1280px)": {
            opacity: isActive ? 1 : 0.7,
            fontSize: "42px"
        },
    }),
    siteSearchBarOuterWrap: {
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
        marginTop: "auto"
    },
    siteSearchBarInnerWrap: {
        marginBottom: "3rem"
    },
    phoneNumberDisplayWrap: {
        marginBottom: "3rem",
        fontSize: "0.875rem",
        lineHeight: "1.25rem"
    },
    phoneNumberDisplay: {
        fontSize: "1.5rem",
        lineHeight: "2rem"
    },
    arrowIcon: {
        marginRight: "1.5rem"
    },
    activeSectionHeading: {
        display: "flex",
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
        marginBottom: "1rem",
        alignItems: "center",
        fontSize: "2.25rem",
        lineHeight: "2.5rem",
        svg: {
            fill: colors.shadesWhite
        }
    },
    icon: {
        marginRight: "0.25rem",
        width: "3rem"
    },
    categoryLinkWrap: {
        display: "flex",
        alignItems: "center",
        whiteSpace: "nowrap"
    },
    activeItemWrap: {
        paddingRight: "2.5rem",
        paddingLeft: "2.5rem",
        marginBottom: "2.25rem"
    },
    activeSection: {
        padding: "65px 0 85px",
        display: "flex",
        position: "absolute",
        top: "0",
        zIndex: 20,
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh",
        backgroundColor: colors.primaryOrange
    },
    subgroupLink: {
        marginLeft: "0.75rem",
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
        fontWeight: 600
    },
    linkSmall: {
        display: "block",
        paddingLeft: "0.25rem",
        marginTop: "0.5rem",
        marginBottom: "0.75rem",
        marginLeft: "3rem",
        color: colors.shadesWhite, fontSize: '21px'
    },
    categoryLink: {
        marginTop: "0.75rem",
        marginBottom: "0.75rem",
        fontSize: "1.5rem",
        lineHeight: "2rem",
        color: colors.shadesWhite
    },
};

export default GlobalNav;
